/**
 * A minimal, dependency-free .xlsx writer.
 *
 * WHY NOT A LIBRARY. ExcelJS is the obvious choice but it depends on Node core
 * modules (`stream`, `buffer`) that webpack 5 no longer polyfills, so it needs
 * build config to work under CRA at all; and this bundle is already past the
 * size webpack warns about. SheetJS's community build can't style cells. The
 * format itself is a ZIP of six small XML parts, which is less code than the
 * workaround would be — so it's written out here.
 *
 * WHAT IT SUPPORTS: one worksheet, inline strings, numbers, dates, per-cell
 * styling (font / fill / border / number format / alignment), column widths,
 * merged ranges, frozen panes, autofilter and print setup. That is exactly
 * what the Month Flow export needs and deliberately no more.
 *
 * WHAT IT DOES NOT: formulas, images, charts, multiple sheets, shared strings.
 * Adding any of those means revisiting the part list in `buildXlsx`.
 *
 * The ZIP is written with the STORE method (no compression). Compression is
 * optional in the spec, every reader accepts stored entries, and it saves
 * pulling in a deflate implementation. Files are a few hundred KB at worst.
 */

// ---------------------------------------------------------------------------
// ZIP (store method)
// ---------------------------------------------------------------------------

/** Standard CRC-32, table built once on first use. */
const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[i] = c >>> 0;
  }
  return table;
})();

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

const utf8 = (s: string): Uint8Array => new TextEncoder().encode(s);

interface ZipEntry {
  name: string;
  data: Uint8Array;
}

/** DOS date/time, as the ZIP header wants it. Seconds have 2-second
 *  resolution in this format — that's the format, not a rounding bug. */
function dosDateTime(d: Date): { time: number; date: number } {
  return {
    time: (d.getHours() << 11) | (d.getMinutes() << 5) | (Math.floor(d.getSeconds() / 2) & 0x1f),
    date: ((d.getFullYear() - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate(),
  };
}

class ByteWriter {
  private chunks: Uint8Array[] = [];
  private len = 0;

  get length() {
    return this.len;
  }

  push(bytes: Uint8Array) {
    this.chunks.push(bytes);
    this.len += bytes.length;
  }

  u16(n: number) {
    const b = new Uint8Array(2);
    new DataView(b.buffer).setUint16(0, n & 0xffff, true);
    this.push(b);
  }

  u32(n: number) {
    const b = new Uint8Array(4);
    new DataView(b.buffer).setUint32(0, n >>> 0, true);
    this.push(b);
  }

  toUint8Array(): Uint8Array {
    const out = new Uint8Array(this.len);
    let offset = 0;
    for (const c of this.chunks) {
      out.set(c, offset);
      offset += c.length;
    }
    return out;
  }
}

function zipStore(entries: ZipEntry[], modified: Date): Uint8Array {
  const { time, date } = dosDateTime(modified);
  const out = new ByteWriter();
  const central: Array<{ name: Uint8Array; crc: number; size: number; offset: number }> = [];

  for (const entry of entries) {
    const name = utf8(entry.name);
    const crc = crc32(entry.data);
    const offset = out.length;

    out.u32(0x04034b50); // local file header
    out.u16(20); // version needed
    out.u16(0x0800); // flags: bit 11 = names are UTF-8
    out.u16(0); // method: store
    out.u16(time);
    out.u16(date);
    out.u32(crc);
    out.u32(entry.data.length); // compressed == uncompressed when stored
    out.u32(entry.data.length);
    out.u16(name.length);
    out.u16(0); // extra length
    out.push(name);
    out.push(entry.data);

    central.push({ name, crc, size: entry.data.length, offset });
  }

  const centralStart = out.length;
  for (const c of central) {
    out.u32(0x02014b50); // central directory header
    out.u16(20); // version made by
    out.u16(20); // version needed
    out.u16(0x0800);
    out.u16(0);
    out.u16(time);
    out.u16(date);
    out.u32(c.crc);
    out.u32(c.size);
    out.u32(c.size);
    out.u16(c.name.length);
    out.u16(0); // extra
    out.u16(0); // comment
    out.u16(0); // disk number
    out.u16(0); // internal attrs
    out.u32(0); // external attrs
    out.u32(c.offset);
    out.push(c.name);
  }
  const centralSize = out.length - centralStart;

  out.u32(0x06054b50); // end of central directory
  out.u16(0);
  out.u16(0);
  out.u16(central.length);
  out.u16(central.length);
  out.u32(centralSize);
  out.u32(centralStart);
  out.u16(0); // comment length

  return out.toUint8Array();
}

// ---------------------------------------------------------------------------
// Cell model
// ---------------------------------------------------------------------------

export type CellValue = string | number | Date | null | undefined;

export interface Cell {
  v: CellValue;
  /** Index into the style registry — use the `S` constants below. */
  s?: number;
  /**
   * A formula, without the leading '=' (e.g. `SUM(C6:C20)`).
   *
   * `v` must still carry the computed result. This writer emits no calcChain
   * part, so a formula with no cached value shows as 0 until the user forces a
   * recalculation — which looks exactly like a broken export. With the value
   * present Excel displays it immediately and recalculates the moment anything
   * the formula depends on is edited.
   */
  f?: string;
}

/** A row is a sparse array; `null`/`undefined` entries leave the cell empty. */
export type Row = Array<Cell | CellValue>;

function isCell(x: Cell | CellValue): x is Cell {
  return typeof x === 'object' && x !== null && !(x instanceof Date) && 'v' in x;
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
    // Excel rejects most control characters outright; strip rather than emit
    // a file that won't open because a bill name carried a stray \x07.
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '');
}

/** 0 → 'A', 25 → 'Z', 26 → 'AA'. */
export function colName(index: number): string {
  let n = index;
  let name = '';
  do {
    name = String.fromCharCode(65 + (n % 26)) + name;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return name;
}

export function cellRef(rowIndex: number, colIndex: number): string {
  return `${colName(colIndex)}${rowIndex + 1}`;
}

/**
 * Excel's serial date: days since 1899-12-30 (the offset absorbs the
 * deliberate 1900-leap-year bug the format carries for Lotus compatibility).
 *
 * Built from the local calendar fields rather than the UTC timestamp, because
 * a date is a calendar day here — using the epoch would shift it a day for
 * anyone west of Greenwich, and Adelaide's half-hour offset makes that class
 * of bug particularly easy to miss.
 */
export function excelSerial(d: Date): number {
  const utcMidnight = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round(utcMidnight / 86400000) + 25569;
}

// ---------------------------------------------------------------------------
// Style registry
// ---------------------------------------------------------------------------

/**
 * Style indices, in the order they're written into styles.xml's <cellXfs>.
 *
 * Kept as a flat named registry rather than a builder: the export needs a
 * dozen looks, they're all known up front, and a named constant at the call
 * site reads better than an options object repeated 400 times.
 */
export const S = {
  default: 0,
  title: 1,
  subtitle: 2,
  sectionHeader: 3,
  tableHeader: 4,
  text: 5,
  textMuted: 6,
  textBold: 7,
  money: 8,
  moneyBold: 9,
  moneyMuted: 10,
  date: 11,
  integer: 12,
  kpiLabel: 13,
  kpiValue: 14,
  kpiValueGood: 15,
  kpiValueBad: 16,
  textBand: 17,
  moneyBand: 18,
  moneyTotal: 19,
  textTotal: 20,
  dateBand: 21,
  integerBand: 22,
} as const;

const STYLES_XML = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <numFmts count="3">
    <numFmt numFmtId="164" formatCode="&quot;$&quot;#,##0.00;[Red]\\-&quot;$&quot;#,##0.00"/>
    <numFmt numFmtId="165" formatCode="d\\ mmm\\ yyyy"/>
    <numFmt numFmtId="166" formatCode="0"/>
  </numFmts>
  <fonts count="9">
    <font><sz val="11"/><color theme="1"/><name val="Calibri"/><family val="2"/></font>
    <font><b/><sz val="18"/><color rgb="FF1F2937"/><name val="Calibri"/><family val="2"/></font>
    <font><sz val="10"/><color rgb="FF6B7280"/><name val="Calibri"/><family val="2"/></font>
    <font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/><family val="2"/></font>
    <font><b/><sz val="11"/><color rgb="FF1F2937"/><name val="Calibri"/><family val="2"/></font>
    <font><sz val="11"/><color rgb="FF6B7280"/><name val="Calibri"/><family val="2"/></font>
    <font><b/><sz val="14"/><color rgb="FF1F2937"/><name val="Calibri"/><family val="2"/></font>
    <font><b/><sz val="14"/><color rgb="FF047857"/><name val="Calibri"/><family val="2"/></font>
    <font><b/><sz val="14"/><color rgb="FFB91C1C"/><name val="Calibri"/><family val="2"/></font>
  </fonts>
  <fills count="6">
    <fill><patternFill patternType="none"/></fill>
    <fill><patternFill patternType="gray125"/></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FF1F2937"/><bgColor indexed="64"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFF3F4F6"/><bgColor indexed="64"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFE5E7EB"/><bgColor indexed="64"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFF9FAFB"/><bgColor indexed="64"/></patternFill></fill>
  </fills>
  <borders count="4">
    <border><left/><right/><top/><bottom/><diagonal/></border>
    <border><left/><right/><top/><bottom style="thin"><color rgb="FFE5E7EB"/></bottom><diagonal/></border>
    <border><left/><right/><top style="thin"><color rgb="FF9CA3AF"/></top><bottom style="double"><color rgb="FF9CA3AF"/></bottom><diagonal/></border>
    <border><left/><right/><top/><bottom style="medium"><color rgb="FF1F2937"/></bottom><diagonal/></border>
  </borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="23">
    <xf numFmtId="0"   fontId="0" fillId="0" borderId="0" xfId="0"/>
    <xf numFmtId="0"   fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment vertical="center"/></xf>
    <xf numFmtId="0"   fontId="2" fillId="0" borderId="0" xfId="0" applyFont="1"/>
    <xf numFmtId="0"   fontId="4" fillId="4" borderId="3" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment vertical="center"/></xf>
    <xf numFmtId="0"   fontId="3" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1" applyAlignment="1"><alignment vertical="center" wrapText="1"/></xf>
    <xf numFmtId="0"   fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1"/>
    <xf numFmtId="0"   fontId="5" fillId="0" borderId="1" xfId="0" applyFont="1" applyBorder="1"/>
    <xf numFmtId="0"   fontId="4" fillId="0" borderId="1" xfId="0" applyFont="1" applyBorder="1"/>
    <xf numFmtId="164" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1"/>
    <xf numFmtId="164" fontId="4" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyBorder="1"/>
    <xf numFmtId="164" fontId="5" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyBorder="1"/>
    <xf numFmtId="165" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1"/>
    <xf numFmtId="166" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center"/></xf>
    <xf numFmtId="0"   fontId="2" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment horizontal="left"/></xf>
    <xf numFmtId="164" fontId="6" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyFont="1"/>
    <xf numFmtId="164" fontId="7" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyFont="1"/>
    <xf numFmtId="164" fontId="8" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyFont="1"/>
    <xf numFmtId="0"   fontId="0" fillId="5" borderId="1" xfId="0" applyFill="1" applyBorder="1"/>
    <xf numFmtId="164" fontId="0" fillId="5" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1"/>
    <xf numFmtId="164" fontId="4" fillId="3" borderId="2" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1"/>
    <xf numFmtId="0"   fontId="4" fillId="3" borderId="2" xfId="0" applyFont="1" applyFill="1" applyBorder="1"/>
    <xf numFmtId="165" fontId="0" fillId="5" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1"/>
    <xf numFmtId="166" fontId="0" fillId="5" borderId="1" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center"/></xf>
  </cellXfs>
  <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;

// ---------------------------------------------------------------------------
// Worksheet
// ---------------------------------------------------------------------------

export interface SheetSpec {
  name: string;
  rows: Row[];
  /** Character widths, one per column. */
  columnWidths?: number[];
  /** e.g. 'A10:P10' — Excel needs an explicit range. */
  autoFilter?: string;
  merges?: string[];
  /** Rows above this index stay put when scrolling. */
  freezeAtRow?: number;
  /** Repeat these sheet rows at the top of every printed page, 1-based
   *  inclusive — e.g. [10, 10] for a single header row. */
  printTitleRows?: [number, number];
  landscape?: boolean;
  /** Squeeze the printed width onto one page. Height is left to run over
   *  pages; forcing both is what makes a long table print unreadably small. */
  fitToWidth?: boolean;
}

function cellXml(rowIndex: number, colIndex: number, cell: Cell | CellValue): string {
  const value: CellValue = isCell(cell) ? cell.v : cell;
  const style = isCell(cell) ? cell.s : undefined;
  const formula = isCell(cell) ? cell.f : undefined;
  const ref = cellRef(rowIndex, colIndex);
  const s = style ? ` s="${style}"` : '';

  if (formula) {
    const cached = typeof value === 'number' && Number.isFinite(value) ? Number(value.toFixed(10)) : 0;
    return `<c r="${ref}"${s}><f>${escapeXml(formula)}</f><v>${cached}</v></c>`;
  }

  if (value === null || value === undefined || value === '') {
    // A styled blank still needs to exist so its fill/border paints.
    return style ? `<c r="${ref}"${s}/>` : '';
  }
  if (value instanceof Date) {
    return `<c r="${ref}"${s}><v>${excelSerial(value)}</v></c>`;
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return style ? `<c r="${ref}"${s}/>` : '';
    // Excel's own precision limit; more digits than this round anyway and
    // exponent notation in the XML is a common cause of "unreadable content".
    return `<c r="${ref}"${s}><v>${Number(value.toFixed(10))}</v></c>`;
  }
  return `<c r="${ref}"${s} t="inlineStr"><is><t xml:space="preserve">${escapeXml(String(value))}</t></is></c>`;
}

function sheetXml(sheet: SheetSpec): string {
  const maxCols = sheet.rows.reduce((m, r) => Math.max(m, r.length), 0);
  const lastRef = cellRef(Math.max(0, sheet.rows.length - 1), Math.max(0, maxCols - 1));

  const cols = sheet.columnWidths?.length
    ? `<cols>${sheet.columnWidths
        .map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`)
        .join('')}</cols>`
    : '';

  const pane =
    sheet.freezeAtRow && sheet.freezeAtRow > 0
      ? `<pane ySplit="${sheet.freezeAtRow}" topLeftCell="A${sheet.freezeAtRow + 1}" activePane="bottomLeft" state="frozen"/>` +
        `<selection pane="bottomLeft" activeCell="A${sheet.freezeAtRow + 1}" sqref="A${sheet.freezeAtRow + 1}"/>`
      : '';

  const body = sheet.rows
    .map((row, r) => {
      const cells = row.map((cell, c) => cellXml(r, c, cell)).join('');
      // Empty rows still need a <row> so the spacing survives.
      return `<row r="${r + 1}">${cells}</row>`;
    })
    .join('');

  // Element order below follows the CT_Worksheet sequence. Excel rejects the
  // file outright if these are out of order — autoFilter must precede
  // mergeCells, and both must precede the print elements.
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<dimension ref="A1:${lastRef}"/>
<sheetViews><sheetView workbookViewId="0" showGridLines="0">${pane}</sheetView></sheetViews>
<sheetFormatPr defaultRowHeight="15"/>
${cols}
<sheetData>${body}</sheetData>
${sheet.autoFilter ? `<autoFilter ref="${sheet.autoFilter}"/>` : ''}
${sheet.merges?.length ? `<mergeCells count="${sheet.merges.length}">${sheet.merges.map((m) => `<mergeCell ref="${m}"/>`).join('')}</mergeCells>` : ''}
<pageMargins left="0.4" right="0.4" top="0.5" bottom="0.5" header="0.3" footer="0.3"/>
<pageSetup orientation="${sheet.landscape ? 'landscape' : 'portrait'}" ${sheet.fitToWidth ? 'fitToWidth="1" fitToHeight="0"' : ''} paperSize="9"/>
</worksheet>`;
}

// ---------------------------------------------------------------------------
// Workbook
// ---------------------------------------------------------------------------

/** Build a single-sheet .xlsx as raw bytes. */
export function buildXlsx(sheet: SheetSpec, modified: Date = new Date()): Uint8Array {
  // Sheet names can't carry these characters or exceed 31 chars; Excel refuses
  // to open the file rather than sanitising for us.
  const safeName = (sheet.name || 'Sheet1').replace(/[\\/*?:[\]]/g, '-').slice(0, 31);

  const definedNames = sheet.printTitleRows
    ? `<definedNames><definedName name="_xlnm.Print_Titles" localSheetId="0">'${safeName.replace(/'/g, "''")}'!$${sheet.printTitleRows[0]}:$${sheet.printTitleRows[1]}</definedName></definedNames>`
    : '';

  const parts: ZipEntry[] = [
    {
      name: '[Content_Types].xml',
      data: utf8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`),
    },
    {
      name: '_rels/.rels',
      data: utf8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`),
    },
    {
      name: 'xl/workbook.xml',
      data: utf8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets><sheet name="${escapeXml(safeName)}" sheetId="1" r:id="rId1"/></sheets>
${definedNames}
</workbook>`),
    },
    {
      name: 'xl/_rels/workbook.xml.rels',
      data: utf8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`),
    },
    { name: 'xl/styles.xml', data: utf8(STYLES_XML) },
    { name: 'xl/worksheets/sheet1.xml', data: utf8(sheetXml(sheet)) },
  ];

  return zipStore(parts, modified);
}

/** Build and hand to the browser as a download. */
export function downloadXlsx(filename: string, sheet: SheetSpec): void {
  const bytes = buildXlsx(sheet);
  // Copy into a fresh ArrayBuffer: Blob rejects a SharedArrayBuffer-backed
  // view under some TS lib configs, and slicing guarantees a plain one.
  const blob = new Blob([bytes.slice().buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
